import React, { useEffect, useId, useState } from 'react';
import {
    Box,
    Button,
    Typography,
    alpha,
    makeStyles,
    useTheme,
} from '@material-ui/core';
import { ArrowLeft, ArrowRight, FileText } from 'react-feather';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import { useTranslation } from 'react-i18next';
import Spinner from 'src/components/Spinner';
import {
    getOutboundPreviewMediaType,
    type TOutboundPreviewMediaType,
} from 'src/views/OmniChannel/utils/outboundMultimedia';
import { InlineAudioPlayer } from '../audio';

const useStyles = makeStyles((theme) => ({
    mediaContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    imageModal: {
        maxWidth: '100%',
        maxHeight: '85vh',
        objectFit: 'contain',
    },
    imageInline: {
        maxWidth: '100%',
        height: 'auto',
        borderRadius: 3,
        cursor: 'pointer',
    },
    videoModal: {
        maxWidth: '100%',
        maxHeight: '85vh',
    },
    videoInline: {
        maxWidth: '100%',
        borderRadius: 3,
    },
    unsupported: {
        textAlign: 'center',
        padding: theme.spacing(3),
    },
    fileTile: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.25),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        maxWidth: 280,
    },
    fileTileLabel: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
}));

export type TMultimediaPreviewContentProps = {
    source: string;
    name: string;
    contentType?: string;
    variant?: 'inline' | 'modal';
    onMediaClick?: () => void;
};

export const MultimediaPreviewContent = ({
    source,
    name,
    contentType,
    variant = 'modal',
    onMediaClick,
}: TMultimediaPreviewContentProps) => {
    const classes = useStyles();
    const theme = useTheme();
    const { t } = useTranslation();
    const reactId = useId();
    const pdfContainerId = `multimedia-pdf-${reactId.replace(/:/g, '')}`;
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [width, setWidth] = useState(100);
    const [pdfError, setPdfError] = useState('');

    const mediaType: TOutboundPreviewMediaType = getOutboundPreviewMediaType(
        name,
        contentType,
        source
    );
    const isInline = variant === 'inline';

    useEffect(() => {
        setPageNumber(1);
        setNumPages(null);
        setPdfError('');
    }, [source, name, contentType]);

    const onDocumentLoadSuccess = ({
        numPages: totalPages,
    }: {
        numPages: number;
    }) => {
        setNumPages(totalPages);
        const container = document.getElementById(pdfContainerId);

        if (container) {
            setWidth(container.offsetWidth);
        }
    };

    const renderFileTile = () => (
        <Box
            className={classes.fileTile}
            role={onMediaClick ? 'button' : undefined}
            tabIndex={onMediaClick ? 0 : undefined}
            onClick={onMediaClick}
            onKeyDown={(event) => {
                if (!onMediaClick) return;

                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onMediaClick();
                }
            }}
        >
            <FileText size={20} />
            <Typography variant="body2" className={classes.fileTileLabel}>
                {name}
            </Typography>
        </Box>
    );

    if (mediaType === 'image') {
        return (
            <Box className={classes.mediaContainer}>
                <img
                    src={source}
                    alt={name}
                    className={
                        isInline ? classes.imageInline : classes.imageModal
                    }
                    onClick={onMediaClick}
                />
            </Box>
        );
    }

    if (mediaType === 'video') {
        if (isInline) {
            return (
                <Box className={classes.mediaContainer}>
                    <video
                        controls
                        src={source}
                        className={classes.videoInline}
                    />
                </Box>
            );
        }

        return (
            <Box className={classes.mediaContainer}>
                <video controls src={source} className={classes.videoModal} />
            </Box>
        );
    }

    if (mediaType === 'audio') {
        return (
            <Box
                className={classes.mediaContainer}
                width={isInline ? '100%' : 420}
            >
                <InlineAudioPlayer
                    src={source}
                    activeColor={theme.palette.text.primary}
                    inactiveColor={alpha(theme.palette.text.primary, 0.45)}
                />
            </Box>
        );
    }

    if (mediaType === 'pdf') {
        if (isInline) {
            return renderFileTile();
        }

        return (
            <Box id={pdfContainerId} width="100%">
                {pdfError ? (
                    <Typography
                        variant="body1"
                        color="textSecondary"
                        align="center"
                    >
                        {pdfError}
                    </Typography>
                ) : (
                    <Document
                        file={source}
                        onLoadSuccess={onDocumentLoadSuccess}
                        loading={<Spinner width={50} />}
                        onLoadError={(error) =>
                            setPdfError(
                                error?.message || t('Chat.PdfLoadError')
                            )
                        }
                    >
                        <Page pageNumber={pageNumber} width={width} />
                        <Box display="flex" mt={2}>
                            <Box flexGrow={1}>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    disabled={pageNumber <= 1}
                                    onClick={() =>
                                        setPageNumber((current) =>
                                            Math.max(1, current - 1)
                                        )
                                    }
                                >
                                    <ArrowLeft />
                                </Button>
                            </Box>
                            <Box>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    disabled={
                                        !numPages || pageNumber >= numPages
                                    }
                                    onClick={() =>
                                        setPageNumber((current) =>
                                            numPages
                                                ? Math.min(
                                                      numPages,
                                                      current + 1
                                                  )
                                                : current + 1
                                        )
                                    }
                                >
                                    <ArrowRight />
                                </Button>
                            </Box>
                        </Box>
                    </Document>
                )}
            </Box>
        );
    }

    if (isInline) {
        return renderFileTile();
    }

    return (
        <Box className={classes.unsupported}>
            <Typography variant="body1" color="textSecondary">
                {t('Chat.PreviewNotAvailable')}
            </Typography>
            <Box mt={2}>
                <Button
                    color="primary"
                    variant="outlined"
                    href={source}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {t('Chat.OpenFile')}
                </Button>
            </Box>
        </Box>
    );
};
